import { useState, useEffect } from 'react';
import { DollarSign, Percent, Truck, CreditCard, Save, RefreshCcw, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminPlatformFees() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fees, setFees] = useState({
    service_fee_percent: 0,
    service_fee_fixed: 0,
    payment_processing_fee_percent: 1.5,
    payment_processing_fee_fixed: 1.00,
    include_fees_in_price: false,
    pass_payment_fees_to_customer: true,
    delivery_fee_per_km: 5.0,
    max_delivery_distance_km: 80,
    flat_fee_beyond_max: 150,
    free_delivery_threshold: 500,
    min_order_amount: 50
  });
  const [baseLocation, setBaseLocation] = useState(null);

  useEffect(() => {
    fetchFees();
  }, []);

  const fetchFees = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/platform-fees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFees({
          service_fee_percent: data.service_fee_percent || 0,
          service_fee_fixed: data.service_fee_fixed || 0,
          payment_processing_fee_percent: data.payment_processing_fee_percent || 1.5,
          payment_processing_fee_fixed: data.payment_processing_fee_fixed || 1.00,
          include_fees_in_price: data.include_fees_in_price || false,
          pass_payment_fees_to_customer: data.pass_payment_fees_to_customer ?? true,
          delivery_fee_per_km: data.delivery_fee_per_km || 5.0,
          max_delivery_distance_km: data.max_delivery_distance_km || 80,
          flat_fee_beyond_max: data.flat_fee_beyond_max || 150,
          free_delivery_threshold: data.free_delivery_threshold || 500,
          min_order_amount: data.min_order_amount || 50
        });
        setBaseLocation(data.base_location);
      }
    } catch (error) {
      toast.error('Failed to load fee settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/platform-fees`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(fees)
      });

      if (response.ok) {
        toast.success('Platform fees updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update fees');
      }
    } catch (error) {
      toast.error('Failed to update fees');
    } finally {
      setSaving(false);
    }
  };

  // Example calculation preview
  const exampleSubtotal = 200;
  const exampleDistance = 15;
  const serviceFee = (exampleSubtotal * fees.service_fee_percent / 100) + fees.service_fee_fixed;
  const paymentFee = fees.pass_payment_fees_to_customer 
    ? ((exampleSubtotal + serviceFee) * fees.payment_processing_fee_percent / 100) + fees.payment_processing_fee_fixed
    : 0;
  const deliveryFee = exampleSubtotal >= fees.free_delivery_threshold 
    ? 0 
    : exampleDistance * fees.delivery_fee_per_km;
  const exampleTotal = exampleSubtotal + serviceFee + paymentFee + deliveryFee;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCcw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const canEdit = ['super_admin', 'manager'].includes(user?.role);

  return (
    <div data-testid="admin-platform-fees">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-foreground flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-primary" />
            Platform Fees
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure service fees, payment fees, and delivery charges
          </p>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      {!canEdit && (
        <Card className="mb-6 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <Info className="h-5 w-5" />
              <p className="text-sm">Only Super Admin or Manager can edit platform fees.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Service Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-blue-600" />
              Service Fees
            </CardTitle>
            <CardDescription>
              Platform service fees added to each order
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="service_fee_percent">Service Fee (%)</Label>
              <Input
                id="service_fee_percent"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={fees.service_fee_percent}
                onChange={(e) => setFees({ ...fees, service_fee_percent: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Percentage of order subtotal
              </p>
            </div>

            <div>
              <Label htmlFor="service_fee_fixed">Fixed Service Fee (R)</Label>
              <Input
                id="service_fee_fixed"
                type="number"
                step="0.01"
                min="0"
                value={fees.service_fee_fixed}
                onChange={(e) => setFees({ ...fees, service_fee_fixed: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Fixed amount added to every order
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label htmlFor="include_fees">Include Fees in Displayed Prices</Label>
                <p className="text-xs text-muted-foreground">
                  Show prices with fees already included
                </p>
              </div>
              <Switch
                id="include_fees"
                checked={fees.include_fees_in_price}
                onCheckedChange={(checked) => setFees({ ...fees, include_fees_in_price: checked })}
                disabled={!canEdit}
              />
            </div>
          </CardContent>
        </Card>

        {/* Payment Processing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-600" />
              Payment Processing
            </CardTitle>
            <CardDescription>
              Paystack fees configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="payment_fee_percent">Payment Fee (%)</Label>
              <Input
                id="payment_fee_percent"
                type="number"
                step="0.1"
                min="0"
                max="10"
                value={fees.payment_processing_fee_percent}
                onChange={(e) => setFees({ ...fees, payment_processing_fee_percent: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paystack charges ~1.5% per transaction
              </p>
            </div>

            <div>
              <Label htmlFor="payment_fee_fixed">Fixed Payment Fee (R)</Label>
              <Input
                id="payment_fee_fixed"
                type="number"
                step="0.01"
                min="0"
                value={fees.payment_processing_fee_fixed}
                onChange={(e) => setFees({ ...fees, payment_processing_fee_fixed: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Fixed transaction fee (typically R1.00)
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label htmlFor="pass_fees">Pass Payment Fees to Customer</Label>
                <p className="text-xs text-muted-foreground">
                  Customer pays payment processing fees
                </p>
              </div>
              <Switch
                id="pass_fees"
                checked={fees.pass_payment_fees_to_customer}
                onCheckedChange={(checked) => setFees({ ...fees, pass_payment_fees_to_customer: checked })}
                disabled={!canEdit}
              />
            </div>
          </CardContent>
        </Card>

        {/* Delivery Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-600" />
              Delivery Fees
            </CardTitle>
            <CardDescription>
              Configure delivery charges for custom addresses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="delivery_fee_km">Fee per Kilometer (R)</Label>
              <Input
                id="delivery_fee_km"
                type="number"
                step="0.50"
                min="0"
                value={fees.delivery_fee_per_km}
                onChange={(e) => setFees({ ...fees, delivery_fee_per_km: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Charged for custom addresses outside pre-loaded locations
              </p>
            </div>

            <div>
              <Label htmlFor="max_distance">Maximum Delivery Distance (km)</Label>
              <Input
                id="max_distance"
                type="number"
                min="1"
                max="200"
                value={fees.max_delivery_distance_km}
                onChange={(e) => setFees({ ...fees, max_delivery_distance_km: parseFloat(e.target.value) || 80 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                From base location: {baseLocation?.address || '19 Richards Drive, Midrand'}
              </p>
            </div>

            <div>
              <Label htmlFor="flat_fee_beyond">Flat Fee Beyond Max Distance (R)</Label>
              <Input
                id="flat_fee_beyond"
                type="number"
                step="10"
                min="0"
                value={fees.flat_fee_beyond_max}
                onChange={(e) => setFees({ ...fees, flat_fee_beyond_max: parseFloat(e.target.value) || 150 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Fixed delivery fee for addresses beyond max distance
              </p>
            </div>

            <div>
              <Label htmlFor="free_threshold">Free Delivery Threshold (R)</Label>
              <Input
                id="free_threshold"
                type="number"
                step="10"
                min="0"
                value={fees.free_delivery_threshold}
                onChange={(e) => setFees({ ...fees, free_delivery_threshold: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Orders above this amount get free delivery
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Order Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-orange-600" />
              Order Settings
            </CardTitle>
            <CardDescription>
              Minimum order requirements
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="min_order">Minimum Order Amount (R)</Label>
              <Input
                id="min_order"
                type="number"
                step="10"
                min="0"
                value={fees.min_order_amount}
                onChange={(e) => setFees({ ...fees, min_order_amount: parseFloat(e.target.value) || 0 })}
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Customers cannot checkout below this amount
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Example Calculation */}
      <Card className="mt-6 bg-slate-50 dark:bg-slate-900/50">
        <CardHeader>
          <CardTitle className="text-lg">Example Calculation Preview</CardTitle>
          <CardDescription>
            Based on R{exampleSubtotal} order with {exampleDistance}km delivery
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>R{exampleSubtotal.toFixed(2)}</span>
            </div>
            {serviceFee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service Fee</span>
                <span>R{serviceFee.toFixed(2)}</span>
              </div>
            )}
            {paymentFee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Payment Processing</span>
                <span>R{paymentFee.toFixed(2)}</span>
              </div>
            )}
            {deliveryFee > 0 ? (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delivery ({exampleDistance}km)</span>
                <span>R{deliveryFee.toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex justify-between text-green-600">
                <span>Delivery</span>
                <span>FREE (order over R{fees.free_delivery_threshold})</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t font-semibold text-base">
              <span>Total</span>
              <span>R{exampleTotal.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
